import React, { PureComponent } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import { BaseInputProps } from '../types';
import { isExpired, isNullLike } from '../utils';

import { Expiries } from './Expiries';
import { NumCvt } from './Num';
import { NumRange } from './NumRange';
import { SettleContext } from '../settleContext';

export interface ExpiryLimitInfo {
  expiry: string;
  minAbs?: number;
  maxAbs?: number;
  minAumPercentage?: number;
  maxAumPercentage?: number;
}

export interface ExpiryLimitProps extends BaseInputProps {
  value?: ExpiryLimitInfo[];

  onChange?: (val: ExpiryLimitInfo[]) => void;

  options?: string[];
}

export class ExpiriesLimit extends PureComponent<ExpiryLimitProps> {
  // 结算上下文：由 ValueDefaultsSelector 按 GLOBAL/ASSET/BOT 作用域注入，用于 expiry 到期判断
  static contextType = SettleContext;
  declare context: React.ContextType<typeof SettleContext>;

  value: ExpiryLimitInfo[] = [];

  isEmptyItem = (item: ExpiryLimitInfo) => {
    return (
      !item.expiry?.trim() &&
      isNullLike(item.minAumPercentage) &&
      isNullLike(item.maxAumPercentage) &&
      isNullLike(item.minAbs) &&
      isNullLike(item.maxAbs)
    );
  };

  onChange = (i: number, val: any, key?: keyof ExpiryLimitInfo) => {
    const value = [...this.value];
    if (!value[i]) value[i] = { expiry: '' };
    if (key) value[i][key] = val as never;
    else value[i] = { ...value[i], ...val };
    this.props.onChange?.(
      value.length === 1 && this.isEmptyItem(value[0]) ? [] : value,
    );
  };

  onAdd = (i: number) => {
    const value = [...this.value];
    value.splice(i + 1, 0, { expiry: '' });
    this.props.onChange?.(value);
    this.props.onBlur?.();
  };

  onDel = (i: number) => {
    const value = [...this.value];
    value.splice(i, 1);
    this.props.onChange?.(
      value.length === 1 && this.isEmptyItem(value[0]) ? [] : value,
    );
    this.props.onBlur?.();
  };

  render() {
    this.value = this.props.value?.filter(it => !isExpired(it.expiry, this.context)) || [];
    // if (!this.value.length && !this.props.readonly)
    //   this.value.push({ expiry: '' });
    return (
      <div
        className={classNames(
          'param-expiries-limit',
          { readonly: this.props.readonly },
          this.props.className,
        )}
        style={this.props.style}
      >
        {this.props.readonly ? (
          this.props.value?.length ? (
            this.value
              .filter(it => it.expiry)
              .map((it, i) => (
                <span className="param-expiries-limit-val" key={i}>
                  {it.expiry}
                  <span className="param-expiries-limit-val-sep">/</span>
                  {!isNullLike(it.minAumPercentage) &&
                  !isNullLike(it.maxAumPercentage)
                    ? `(${NumCvt.show(
                        it.minAumPercentage,
                        true,
                      )}%, ${NumCvt.show(it.maxAumPercentage, true)}%)`
                    : '-'}
                  <span className="param-expiries-limit-val-sep">/</span>
                  {!isNullLike(it.minAbs) && !isNullLike(it.maxAbs)
                    ? `(${it.minAbs}, ${it.maxAbs})`
                    : '-'}
                </span>
              ))
          ) : (
            '-'
          )
        ) : (
          <div className="grid grid-cols-8 gap-4 w-full">
            {/* 表头 */}
            <div className="font-medium text-gray-700 rounded-md col-span-2">
              Expiry
            </div>
            <div className="font-medium text-gray-700 rounded-md col-span-2">
              Limit(% of AUM)
            </div>
            <div className="font-medium text-gray-700 rounded-md col-span-2">
              Limit(ABS)
            </div>
            {
              this.value?.length > 0 ? <div className='col-span-2' /> :  <div className="flex items-center col-span-2">
              <button
                onClick={() => this.onAdd(0)}
                className="p-1 rounded hover:bg-blue-100 transition-colors"
                aria-label="Add item"
              >
                <PlusOutlined />
              </button>
            </div>
            }
            
            {/* 数据行 */}
            {this.value.map((it, i) => (
              <React.Fragment key={i}>
                {/* Expiry */}
                <div className="col-span-2">
                  <Expiries
                    className="w-full"
                    multiple={false}
                    options={this.props.options}
                    value={[it.expiry]}
                    onChange={val => this.onChange(i, val[0], 'expiry')}
                    calcDisabled={expiry =>
                      this.value.some(
                        item => item !== it && item.expiry === expiry,
                      )
                    }
                    onBlur={this.props.onBlur}
                  />
                </div>
                
                {/* Limit(% of AUM) */}
                <div className="col-span-2">
                  <NumRange
                    className="w-full"
                    value={{ min: it.minAumPercentage, max: it.maxAumPercentage }}
                    onChange={val =>
                      this.onChange(i, {
                        minAumPercentage: val?.min,
                        maxAumPercentage: val?.max,
                      })
                    }
                    isPercentage
                    onBlur={this.props.onBlur}
                  />
                </div>
                
                {/* Limit(ABS) */}
                <div className="col-span-2">
                  <NumRange
                    className="w-full"
                    value={{ min: it.minAbs, max: it.maxAbs }}
                    onChange={val =>
                      this.onChange(i, { minAbs: val?.min, maxAbs: val?.max })
                    }
                    onBlur={this.props.onBlur}
                  />
                </div>
                
                {/* 操作按钮 */}
                <div className="col-span-2">
                  <button
                    onClick={() => this.onAdd(i)}
                    className="p-1 rounded hover:bg-blue-100 transition-colors"
                    aria-label="Add item"
                  >
                    <PlusOutlined />
                  </button>
                  <button
                    onClick={() => this.onDel(i)}
                    className="p-1 rounded hover:bg-red-100 transition-colors"
                    aria-label="Delete item"
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }
}
